import { useAssistantStore } from '@/stores/useAssistantStore';
import { useOrbitalAnimation } from '@/hooks/useOrbitalAnimation';
import { motion } from 'framer-motion';
import {
  MessageSquare, Layers, Activity, Settings, Orbit,
  Music, Volume2, Command, Wand2
} from 'lucide-react';

const DOCK_ITEMS = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'vstack', label: 'VSTACK', icon: Layers },
  { id: 'status', label: 'Status', icon: Activity },
  { id: 'daw', label: 'DAW', icon: Music },
  { id: 'tts', label: 'TTS', icon: Volume2 },
  { id: 'commands', label: 'Commands', icon: Command },
  { id: 'ai', label: 'AI Wand', icon: Wand2 },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export function OrbitalDock() {
  const { activeDock, setActiveDock, panelOpen, togglePanel } = useAssistantStore();
  const { rotation, particles } = useOrbitalAnimation();

  return (
    <div className="relative flex flex-col items-center">
      {/* Orbital Ring Visual */}
      <div className="relative w-20 h-20 mb-2">
        {/* Outer ring */}
        <div
          className="absolute inset-0 orbital-ring opacity-30"
          style={{ transform: `rotate(${rotation}deg)` }}
        />
        {/* Inner ring */}
        <div
          className="absolute inset-2 orbital-ring opacity-20"
          style={{ transform: `rotate(${-rotation * 1.5}deg)` }}
        />
        {/* Center logo */}
        <button
          onClick={togglePanel}
          className="absolute inset-4 rounded-full bg-violet/20 border border-violet/40 flex items-center justify-center hover:bg-violet/30 transition-all hover:shadow-glow-violet"
        >
          <Orbit size={20} className="text-violet" />
        </button>
        {/* Particles */}
        {particles.slice(0, 6).map((p) => (
          <div
            key={p.id}
            className="absolute w-1 h-1 rounded-full bg-violet/40 animate-particle-rise"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              animationDelay: `${p.delay}s`
            }}
          />
        ))}
      </div>

      {/* Dock Bar */}
      <motion.div
        className="flex items-center gap-1 px-2 py-1.5 rounded-2xl glass-panel"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {DOCK_ITEMS.map((item) => {
          const isActive = activeDock === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setActiveDock(item.id);
                if (!panelOpen) togglePanel();
              }}
              className={`dock-item w-10 h-10 ${isActive ? 'active' : ''}`}
              title={item.label}
            >
              <item.icon
                size={18}
                className={`transition-colors ${
                  isActive ? 'text-spice' : 'text-secondary'
                }`}
              />
              {isActive && (
                <motion.div
                  layoutId="dock-indicator"
                  className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-spice"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
            </button>
          );
        })}
      </motion.div>

      {/* Active Label */}
      <span className="mt-1.5 text-[10px] uppercase tracking-widest text-tertiary">
        {DOCK_ITEMS.find((d) => d.id === activeDock)?.label || 'APE_XØRBIT'}
      </span>
    </div>
  );
}
